import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable, Badge } from "@/components/common/DataTable";
import { commTemplateService, tenantService, channelService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Loader2, Send, RefreshCw } from "lucide-react";

const CHANNEL_TYPES = ["any", "webchat", "email", "sms", "whatsapp", "instagram", "facebook", "voice"];
const EMPTY = { name: "", channelType: "any", category: "General", language: "en", body: "", tenantId: "" };
const APPROVAL_COLOR = { approved: "green", pending: "amber", rejected: "red", not_submitted: "slate" };
const APPROVAL_LABEL = { approved: "Approved", pending: "Pending", rejected: "Rejected", not_submitted: "Not submitted" };
const WA_CATEGORIES = ["UTILITY", "MARKETING", "AUTHENTICATION"];

export default function CommTemplatesPage() {
  const { hasPermission, user } = useAuth();
  const isSuper = user?.isSuperAdmin;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [submitTpl, setSubmitTpl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await commTemplateService.list(); setRows(d.items || []); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isSuper) tenantService.list().then((d) => setTenants(d.items || [])).catch(() => {}); }, [isSuper]);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...EMPTY, ...r }); setOpen(true); };

  const deriveVars = (body) => Array.from(new Set((body.match(/{{\s*([\w.]+)\s*}}/g) || []).map((m) => m.replace(/[{}\s]/g, ""))));

  const save = async () => {
    if (!form.name || !form.body) return toast.error("Name and body are required");
    if (isSuper && !editing && !form.tenantId) return toast.error("Select a tenant");
    setSaving(true);
    try {
      const variables = deriveVars(form.body);
      if (editing) { const { name, channelType, category, language, body } = form; await commTemplateService.update(editing.id, { name, channelType, category, language, body, variables }); toast.success("Template updated"); }
      else { await commTemplateService.create({ ...form, variables }); toast.success("Template created"); }
      setOpen(false); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete template "${r.name}"?`)) return;
    try { await commTemplateService.remove(r.id); toast.success("Template deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const canCreate = hasPermission("COMM_TEMPLATE.CREATE");
  const canEdit = hasPermission("COMM_TEMPLATE.EDIT");
  const canDelete = hasPermission("COMM_TEMPLATE.DELETE");
  const canConnectWa = hasPermission("WHATSAPP.CONNECT");

  const syncStatus = async (r) => {
    if (!r.submittedChannelId) return;
    try {
      const d = await commTemplateService.whatsappStatus(r.id, r.submittedChannelId);
      toast.success(`Status: ${APPROVAL_LABEL[d.approvalStatus] || d.approvalStatus}`); load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const columns = [
    { key: "name", header: "Template", render: (r) => <span className="font-medium text-foreground">{r.name}</span> },
    { key: "channelType", header: "Channel", render: (r) => <Badge color="blue">{r.channelType}</Badge> },
    { key: "category", header: "Category", render: (r) => r.category || "—" },
    { key: "status", header: "Status", render: (r) => <Badge color={r.status === "Active" ? "green" : "slate"}>{r.status}</Badge> },
    { key: "approvalStatus", header: "WhatsApp Approval", render: (r) => <Badge color={APPROVAL_COLOR[r.approvalStatus] || "slate"}>{APPROVAL_LABEL[r.approvalStatus] || r.approvalStatus || "—"}</Badge> },
    {
      key: "_a", header: "",
      render: (r) => {
        const waEligible = ["whatsapp", "any"].includes(r.channelType);
        return (
          <div className="flex items-center justify-end gap-1">
            {waEligible && canConnectWa && r.approvalStatus !== "approved" && (
              <button onClick={() => setSubmitTpl(r)} className="p-1.5 rounded-md hover:bg-green-500/10 text-green-600" title="Submit to WhatsApp" data-testid={`tpl-wa-submit-${r.id}`}><Send className="h-4 w-4" /></button>
            )}
            {waEligible && r.submittedChannelId && (
              <button onClick={() => syncStatus(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" title="Sync WhatsApp status" data-testid={`tpl-wa-sync-${r.id}`}><RefreshCw className="h-4 w-4" /></button>
            )}
            {canEdit && <button onClick={() => openEdit(r)} className="p-1.5 rounded-md hover:bg-muted text-muted-foreground" data-testid={`tpl-edit-${r.id}`}><Pencil className="h-4 w-4" /></button>}
            {canDelete && <button onClick={() => remove(r)} className="p-1.5 rounded-md hover:bg-destructive/10 text-destructive" data-testid={`tpl-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}
          </div>
        );
      },
    },
  ];

  return (
    <div data-testid="comm-templates-page">
      <PageHeader title="Message Templates" subtitle="Reusable templates with variables · submit WhatsApp templates to Meta for approval"
        actions={canCreate && (
          <button onClick={openCreate} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2 text-sm font-semibold hover:bg-primary/90 transition-colors" data-testid="tpl-create-btn">
            <Plus className="h-4 w-4" /> New Template
          </button>
        )}
      />
      <DataTable columns={columns} rows={rows} loading={loading} testid="comm-templates-table" empty="No templates yet" />

      {open && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-card text-foreground rounded-2xl w-full max-w-lg p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="tpl-dialog">
            <h3 className="text-lg font-bold mb-4">{editing ? "Edit" : "New"} Template</h3>
            <div className="space-y-3.5">
              {isSuper && !editing && (
                <div>
                  <label className="block text-sm font-medium mb-1">Tenant *</label>
                  <select value={form.tenantId} onChange={(e) => setForm({ ...form, tenantId: e.target.value })} className="ciq-input" data-testid="tpl-tenant">
                    <option value="">Select tenant…</option>
                    {tenants.map((t) => <option key={t.id} value={t.id}>{t.tenantName || t.name || t.id}</option>)}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Name *</label><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="ciq-input" data-testid="tpl-name" /></div>
                <div><label className="block text-sm font-medium mb-1">Channel</label><select value={form.channelType} onChange={(e) => setForm({ ...form, channelType: e.target.value })} className="ciq-input">{CHANNEL_TYPES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium mb-1">Category</label><input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="ciq-input" /></div>
                <div><label className="block text-sm font-medium mb-1">Language</label><input value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })} className="ciq-input" /></div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Body <span className="text-muted-foreground font-normal">(use {"{{variable}}"})</span></label>
                <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="ciq-input" rows={4} data-testid="tpl-body" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
              <button onClick={save} disabled={saving} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="tpl-save">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </div>
        </div>
      )}
      {submitTpl && <SubmitWhatsAppModal template={submitTpl} onClose={() => setSubmitTpl(null)} onDone={() => { setSubmitTpl(null); load(); }} />}
    </div>
  );
}

function SubmitWhatsAppModal({ template, onClose, onDone }) {
  const [channels, setChannels] = useState([]);
  const [channelId, setChannelId] = useState("");
  const [category, setCategory] = useState("UTILITY");
  const [language, setLanguage] = useState(template.language || "en_US");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    channelService.list({ tenantId: template.tenantId }).then((d) => {
      const wa = (d.items || []).filter((c) => c.providerType === "whatsapp" && c.status === "Connected");
      setChannels(wa);
      if (wa[0]) setChannelId(wa[0].id);
    }).catch(() => {});
  }, [template.tenantId]);

  const submit = async () => {
    if (!channelId) return toast.error("Connect a WhatsApp channel first");
    setSubmitting(true);
    try {
      const d = await commTemplateService.submitWhatsapp(template.id, { channelId, category, language });
      toast.success(d.sandbox ? `Submitted (sandbox → ${APPROVAL_LABEL[d.approvalStatus]})` : "Submitted to WhatsApp for approval");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-card text-foreground rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="tpl-wa-submit-modal">
        <h3 className="text-lg font-bold mb-1">Submit to WhatsApp</h3>
        <p className="text-sm text-muted-foreground mb-4">Template: <span className="font-medium text-foreground">{template.name}</span></p>
        <div className="space-y-3.5">
          <div>
            <label className="block text-sm font-medium mb-1">WhatsApp Channel *</label>
            <select value={channelId} onChange={(e) => setChannelId(e.target.value)} className="ciq-input" data-testid="tpl-wa-channel">
              <option value="">{channels.length ? "Select channel…" : "No connected WhatsApp channel"}</option>
              {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-sm font-medium mb-1">Category</label><select value={category} onChange={(e) => setCategory(e.target.value)} className="ciq-input" data-testid="tpl-wa-category">{WA_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-1">Language</label><input value={language} onChange={(e) => setLanguage(e.target.value)} className="ciq-input" data-testid="tpl-wa-language" /></div>
          </div>
          <p className="text-xs text-muted-foreground">Meta requires lowercase names &amp; approved categories. Variables are converted to positional placeholders automatically.</p>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg border border-border hover:bg-muted">Cancel</button>
          <button onClick={submit} disabled={submitting || !channelId} className="px-4 py-2 text-sm rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 flex items-center gap-2 disabled:opacity-60" data-testid="tpl-wa-submit-confirm">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />} Submit
          </button>
        </div>
      </div>
    </div>
  );
}
